import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Mail,
  MoreVertical,
  Pencil,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UserCheck,
  UserMinus,
  UserPlus,
  UsersRound,
  XCircle,
} from "lucide-react";

const EMPTY_INVITE = {
  name: "",
  email: "",
  job_title: "",
  role: "member",
  team_id: "none",
  permissions: [],
};

const EMPTY_TEAM = {
  name: "",
  region: "Global",
  description: "",
  lead_id: "none",
  member_ids: [],
  status: "active",
};

export default function TeamAccess() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const currentUserId = user?.id;
  const [data, setData] = useState({ teams: [], users: [], invitations: [] });
  const [options, setOptions] = useState({ roles: [], permissions: [] });
  const [section, setSection] = useState("people");
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [invite, setInvite] = useState(EMPTY_INVITE);
  const [team, setTeam] = useState(EMPTY_TEAM);
  const [access, setAccess] = useState(EMPTY_INVITE);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [overview, choices] = await Promise.all([
        api.get("/team-access"),
        api.get("/team-access/options"),
      ]);
      setData(overview.data);
      setOptions(choices.data);
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      load();
    } else {
      setLoading(false);
    }
  }, [isSuperAdmin, load]);

  const role = useMemo(
    () => options.roles.find((item) => item.key === invite.role),
    [invite.role, options.roles],
  );
  const accessRole = useMemo(
    () => options.roles.find((item) => item.key === access.role),
    [access.role, options.roles],
  );

  const applyRole = (target, value, setter) => {
    const selected = options.roles.find((item) => item.key === value);
    setter({
      ...target,
      role: value,
      permissions: selected?.default_permissions?.includes("*")
        ? []
        : selected?.default_permissions || [],
    });
  };

  const togglePermission = (target, permission, setter) => {
    setter({
      ...target,
      permissions: target.permissions.includes(permission)
        ? target.permissions.filter((item) => item !== permission)
        : [...target.permissions, permission],
    });
  };

  const openInvite = () => {
    const member = options.roles.find((item) => item.key === "member");
    setInvite({
      ...EMPTY_INVITE,
      permissions: member?.default_permissions || [],
    });
    setInviteOpen(true);
  };

  const submitInvite = async () => {
    if (!invite.name.trim() || !invite.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/team-access/invitations", {
        ...invite,
        team_id: invite.team_id === "none" ? null : invite.team_id,
      });
      toast.success(`Invitation sent to ${invite.email}`);
      setInviteOpen(false);
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const openTeam = (current = null) => {
    setEditingTeam(current);
    setTeam(
      current
        ? {
            name: current.name,
            region: current.region || "Global",
            description: current.description || "",
            lead_id: current.lead_id || "none",
            member_ids: (current.members || []).map((member) => member.id),
            status: current.status || "active",
          }
        : EMPTY_TEAM,
    );
    setTeamOpen(true);
  };

  const toggleTeamMember = (id) => {
    setTeam((current) => ({
      ...current,
      member_ids: current.member_ids.includes(id)
        ? current.member_ids.filter((memberId) => memberId !== id)
        : [...current.member_ids, id],
    }));
  };

  const submitTeam = async () => {
    if (!team.name.trim()) {
      toast.error("A team name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...team,
        lead_id: team.lead_id === "none" ? null : team.lead_id,
      };
      if (editingTeam) {
        await api.put(`/team-access/teams/${editingTeam.id}`, payload);
      } else {
        await api.post("/team-access/teams", payload);
      }
      toast.success(editingTeam ? "Team updated" : "Team created");
      setTeamOpen(false);
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const openAccess = (current) => {
    setEditingUser(current);
    setAccess({
      job_title: current.job_title || "",
      role: current.role,
      team_id: current.team_id || "none",
      permissions: current.permissions?.includes("*")
        ? []
        : current.permissions || [],
    });
    setAccessOpen(true);
  };

  const submitAccess = async () => {
    setSaving(true);
    try {
      await api.put(`/team-access/users/${editingUser.id}/access`, {
        ...access,
        team_id: access.team_id === "none" ? null : access.team_id,
      });
      toast.success("Access updated");
      setAccessOpen(false);
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  const invitationAction = async (id, action) => {
    try {
      await api.post(`/team-access/invitations/${id}/${action}`);
      toast.success(
        action === "resend" ? "Invitation resent" : "Invitation revoked",
      );
      load();
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setSaving(true);
    try {
      if (confirm.type === "delete") {
        await api.delete(`/team-access/users/${confirm.user.id}`);
        toast.success(`${confirm.user.name} removed from Nexreq ATS`);
      } else if (confirm.type === "offboard") {
        await api.post(`/team-access/users/${confirm.user.id}/offboard`);
        toast.success(`${confirm.user.name} offboarded — access revoked`);
      } else if (confirm.type === "reactivate") {
        await api.post(`/team-access/users/${confirm.user.id}/reactivate`);
        toast.success(`${confirm.user.name} reactivated`);
      }
      setConfirm(null);
      load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div
        className="p-8 text-sm text-muted-foreground"
        data-testid="team-access-loading"
      >
        Loading Team & Access…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-access-page">
      <PageHeader
        title="Team & Access"
        subtitle={
          "Create every internal account, organise teams, and control access before an " +
          "invitation is sent."
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => openTeam()}
              data-testid="team-access-create-team-button"
            >
              <UsersRound className="mr-1.5 h-4 w-4" /> New Team
            </Button>
            <Button
              onClick={openInvite}
              data-testid="team-access-invite-button"
              className="bg-[#255058] text-white hover:bg-[#1c3d43]"
            >
              <UserPlus className="mr-1.5 h-4 w-4" /> Invite User
            </Button>
          </div>
        }
      />

      <div
        className="grid gap-3 sm:grid-cols-3"
        data-testid="team-access-metrics"
      >
        <Metric
          label="Active teammates"
          value={data.users.filter((user) => user.is_active !== false).length}
        />
        <Metric
          label="Teams"
          value={data.teams.filter((item) => item.status === "active").length}
        />
        <Metric
          label="Pending invites"
          value={
            data.invitations.filter((item) => item.status === "pending").length
          }
        />
      </div>

      <div
        className="flex flex-wrap gap-2 border-b border-border pb-3"
        role="tablist"
        data-testid="team-access-tabs"
      >
        {[
          ["people", "People"],
          ["teams", "Teams"],
          ["invitations", "Invitations"],
        ].map(([value, label]) => (
          <Button
            key={value}
            variant={section === value ? "default" : "outline"}
            onClick={() => setSection(value)}
            data-testid={`team-access-tab-${value}`}
            className={
              section === value ? "bg-[#255058] hover:bg-[#1c3d43]" : ""
            }
          >
            {label}
          </Button>
        ))}
      </div>

      {section === "people" && (
        <Card
          className="overflow-hidden"
          data-testid="team-access-people-table"
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Person</TableHead>
                  <TableHead>Access role</TableHead>
                  <TableHead className="hidden md:table-cell">Team</TableHead>
                  <TableHead className="hidden lg:table-cell">
                    Feature access
                  </TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.users.map((user) => (
                  <TableRow
                    key={user.id}
                    data-testid={`team-access-user-row-${user.id}`}
                  >
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{user.name}</span>
                        {user.is_active === false && (
                          <Badge
                            variant="outline"
                            className="mt-0.5 w-fit border-amber-300 bg-amber-50 text-[10px] text-amber-700"
                            data-testid={`team-access-offboarded-badge-${user.id}`}
                          >
                            Offboarded
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {user.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {roleLabel(user.role, options.roles)}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {user.team_name || "Unassigned"}
                    </TableCell>
                    <TableCell className="hidden max-w-xs lg:table-cell">
                      <AccessSummary permissions={user.permissions} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openAccess(user)}
                          data-testid={`team-access-edit-user-${user.id}`}
                        >
                          <Pencil className="mr-1 h-3.5 w-3.5" /> Access
                        </Button>
                        {user.id !== currentUserId && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="px-2"
                                data-testid={`team-access-user-actions-${user.id}`}
                              >
                                <MoreVertical className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {user.is_active === false ? (
                                <DropdownMenuItem
                                  onClick={() =>
                                    setConfirm({ type: "reactivate", user })
                                  }
                                  data-testid={`team-access-reactivate-user-${user.id}`}
                                >
                                  <UserCheck className="mr-2 h-4 w-4" /> Reactivate
                                  access
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  onClick={() =>
                                    setConfirm({ type: "offboard", user })
                                  }
                                  data-testid={`team-access-offboard-user-${user.id}`}
                                >
                                  <UserMinus className="mr-2 h-4 w-4" /> Offboard
                                  (remove access)
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-600"
                                onClick={() =>
                                  setConfirm({ type: "delete", user })
                                }
                                data-testid={`team-access-delete-user-${user.id}`}
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Delete person
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {section === "teams" && (
        <Card className="overflow-hidden" data-testid="team-access-teams-table">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Team</TableHead>
                  <TableHead className="hidden md:table-cell">Region</TableHead>
                  <TableHead>Members</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.teams.map((item) => (
                  <TableRow
                    key={item.id}
                    data-testid={`team-access-team-row-${item.id}`}
                  >
                    <TableCell>
                      <div className="font-medium">{item.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {item.description || "No description"}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {item.region}
                    </TableCell>
                    <TableCell>{item.member_count}</TableCell>
                    <TableCell>
                      <StatusBadge value={item.status} />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openTeam(item)}
                        data-testid={`team-access-edit-team-${item.id}`}
                      >
                        <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {section === "invitations" && (
        <Card
          className="overflow-hidden"
          data-testid="team-access-invitations-table"
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invitee</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="hidden md:table-cell">Team</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.invitations.map((item) => (
                  <TableRow
                    key={item.id}
                    data-testid={`team-access-invitation-row-${item.id}`}
                  >
                    <TableCell>
                      <div className="font-medium">{item.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {item.email}
                      </div>
                    </TableCell>
                    <TableCell>{item.role_label}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      {item.team_name || "Unassigned"}
                    </TableCell>
                    <TableCell>
                      <StatusBadge value={item.status} />
                    </TableCell>
                    <TableCell>
                      {item.status === "pending" ? (
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => invitationAction(item.id, "resend")}
                            data-testid={`team-access-resend-${item.id}`}
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => invitationAction(item.id, "revoke")}
                            data-testid={`team-access-revoke-${item.id}`}
                          >
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <InviteDialog
        invite={invite}
        open={inviteOpen}
        options={options}
        teams={data.teams}
        role={role}
        saving={saving}
        onChange={setInvite}
        onClose={() => setInviteOpen(false)}
        onRoleChange={(value) => applyRole(invite, value, setInvite)}
        onTogglePermission={(permission) =>
          togglePermission(invite, permission, setInvite)
        }
        onSubmit={submitInvite}
      />

      <TeamDialog
        editing={editingTeam}
        open={teamOpen}
        team={team}
        users={data.users}
        saving={saving}
        onChange={setTeam}
        onClose={() => setTeamOpen(false)}
        onToggleMember={toggleTeamMember}
        onSubmit={submitTeam}
      />

      <AccessDialog
        access={access}
        open={accessOpen}
        options={options}
        teams={data.teams}
        role={accessRole}
        user={editingUser}
        saving={saving}
        onChange={setAccess}
        onClose={() => setAccessOpen(false)}
        onRoleChange={(value) => applyRole(access, value, setAccess)}
        onTogglePermission={(permission) =>
          togglePermission(access, permission, setAccess)
        }
        onSubmit={submitAccess}
      />

      <ConfirmActionDialog
        confirm={confirm}
        saving={saving}
        onClose={() => setConfirm(null)}
        onConfirm={runConfirm}
      />
    </div>
  );
}

const CONFIRM_COPY = {
  offboard: {
    title: "Offboard this person?",
    action: "Offboard",
    destructive: true,
    describe: (name) =>
      `${name} will immediately lose all access to Nexreq ATS and be signed out. ` +
      "Their record stays so you can reactivate them later.",
  },
  reactivate: {
    title: "Reactivate this person?",
    action: "Reactivate",
    destructive: false,
    describe: (name) => `${name} will regain sign-in access to Nexreq ATS.`,
  },
  delete: {
    title: "Delete this person permanently?",
    action: "Delete",
    destructive: true,
    describe: (name) =>
      `${name} will be permanently removed from Nexreq ATS, including their ` +
      "login and team memberships. This cannot be undone.",
  },
};

function ConfirmActionDialog({ confirm, saving, onClose, onConfirm }) {
  const copy = confirm ? CONFIRM_COPY[confirm.type] : null;
  return (
    <AlertDialog open={!!confirm} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent data-testid="team-access-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{copy?.title}</AlertDialogTitle>
          <AlertDialogDescription>
            {copy && confirm ? copy.describe(confirm.user.name) : ""}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="team-access-confirm-cancel">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              onConfirm();
            }}
            disabled={saving}
            className={
              copy?.destructive
                ? "bg-red-600 text-white hover:bg-red-700"
                : "bg-[#255058] text-white hover:bg-[#1c3d43]"
            }
            data-testid="team-access-confirm-action"
          >
            {saving ? "Working…" : copy?.action}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function InviteDialog({
  invite,
  open,
  options,
  teams,
  role,
  saving,
  onChange,
  onClose,
  onRoleChange,
  onTogglePermission,
  onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Invite teammate</DialogTitle>
          <DialogDescription>
            Access is configured before Nexreq sends a secure one-time
            invitation.
          </DialogDescription>
        </DialogHeader>
        <InviteFields
          prefix="invite"
          value={invite}
          roles={options.roles}
          permissions={options.permissions}
          teams={teams}
          selectedRole={role}
          onChange={onChange}
          onRoleChange={onRoleChange}
          onTogglePermission={onTogglePermission}
        />
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="team-access-invite-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving}
            data-testid="team-access-send-invitation-button"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            <Mail className="mr-1.5 h-4 w-4" />{" "}
            {saving ? "Sending…" : "Send invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccessDialog({
  access,
  open,
  options,
  teams,
  role,
  user,
  saving,
  onChange,
  onClose,
  onRoleChange,
  onTogglePermission,
  onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Access for {user?.name}</DialogTitle>
          <DialogDescription>
            Changes apply to the active account immediately.
          </DialogDescription>
        </DialogHeader>
        <InviteFields
          prefix="access"
          value={access}
          roles={options.roles}
          permissions={options.permissions}
          teams={teams}
          selectedRole={role}
          editMode
          onChange={onChange}
          onRoleChange={onRoleChange}
          onTogglePermission={onTogglePermission}
        />
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="team-access-access-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving}
            data-testid="team-access-save-access-button"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            {saving ? "Saving…" : "Save access"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteFields({
  prefix,
  value,
  roles,
  permissions,
  teams,
  selectedRole,
  editMode,
  onChange,
  onRoleChange,
  onTogglePermission,
}) {
  const full = selectedRole?.default_permissions?.includes("*");
  return (
    <div className="space-y-4">
      {!editMode && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor={`${prefix}-name`}>Name</Label>
            <Input
              id={`${prefix}-name`}
              value={value.name}
              onChange={(event) =>
                onChange({ ...value, name: event.target.value })
              }
              data-testid={`team-access-${prefix}-name`}
            />
          </div>
          <div>
            <Label htmlFor={`${prefix}-email`}>Work email</Label>
            <Input
              id={`${prefix}-email`}
              type="email"
              value={value.email}
              onChange={(event) =>
                onChange({ ...value, email: event.target.value })
              }
              data-testid={`team-access-${prefix}-email`}
            />
          </div>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <Label htmlFor={`${prefix}-job-title`}>Job title</Label>
          <Input
            id={`${prefix}-job-title`}
            value={value.job_title}
            onChange={(event) =>
              onChange({ ...value, job_title: event.target.value })
            }
            data-testid={`team-access-${prefix}-job-title`}
          />
        </div>
        <div>
          <Label>Access role</Label>
          <Select value={value.role} onValueChange={onRoleChange}>
            <SelectTrigger data-testid={`team-access-${prefix}-role`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {roles.map((item) => (
                <SelectItem key={item.key} value={item.key}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Team</Label>
          <Select
            value={value.team_id}
            onValueChange={(team_id) => onChange({ ...value, team_id })}
          >
            <SelectTrigger data-testid={`team-access-${prefix}-team`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Assign later</SelectItem>
              {teams
                .filter((item) => item.status === "active")
                .map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div
        className="border-t border-border pt-4"
        data-testid={`team-access-${prefix}-permissions`}
      >
        <div className="mb-3 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[#255058]" />
          <div>
            <div className="text-sm font-semibold">Feature access</div>
            <div className="text-xs text-muted-foreground">
              Only enabled access is granted for this account.
            </div>
          </div>
        </div>
        {full ? (
          <div
            className={
              "rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm " +
              "text-emerald-800"
            }
            data-testid={`team-access-${prefix}-full-access`}
          >
            Full unrestricted access is required for the Super Admin role.
          </div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {permissions.map((permission) => (
              <label
                key={permission.key}
                className={
                  "flex items-center justify-between gap-3 rounded-md border border-border " +
                  "px-3 py-2 text-sm"
                }
              >
                <span>{permission.label}</span>
                <Switch
                  checked={value.permissions.includes(permission.key)}
                  onCheckedChange={() => onTogglePermission(permission.key)}
                  data-testid={
                    `team-access-${prefix}-permission-${permission.key.replace(":", "-")}`
                  }
                />
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TeamDialog({
  editing,
  open,
  team,
  users,
  saving,
  onChange,
  onClose,
  onToggleMember,
  onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit team" : "Create team"}</DialogTitle>
          <DialogDescription>
            Teams organise internal staff. Access remains configured per person.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="team-access-team-name">Team name</Label>
              <Input
                id="team-access-team-name"
                value={team.name}
                onChange={(event) =>
                  onChange({ ...team, name: event.target.value })
                }
                data-testid="team-access-team-name"
              />
            </div>
            <div>
              <Label htmlFor="team-access-team-region">Region</Label>
              <Input
                id="team-access-team-region"
                value={team.region}
                onChange={(event) =>
                  onChange({ ...team, region: event.target.value })
                }
                data-testid="team-access-team-region"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="team-access-team-description">Description</Label>
            <Textarea
              id="team-access-team-description"
              value={team.description}
              onChange={(event) =>
                onChange({ ...team, description: event.target.value })
              }
              data-testid="team-access-team-description"
            />
          </div>
          <div>
            <Label>Team lead</Label>
            <Select
              value={team.lead_id}
              onValueChange={(lead_id) => onChange({ ...team, lead_id })}
            >
              <SelectTrigger data-testid="team-access-team-lead">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Assign later</SelectItem>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Members</Label>
            <div
              className={
                "mt-2 max-h-64 space-y-1 overflow-y-auto rounded-md border border-border " +
                "p-2"
              }
              data-testid="team-access-team-members"
            >
              {users.map((user) => (
                <label
                  key={user.id}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
                >
                  <Checkbox
                    checked={team.member_ids.includes(user.id)}
                    onCheckedChange={() => onToggleMember(user.id)}
                    data-testid={`team-access-team-member-${user.id}`}
                  />
                  <span className="flex-1">{user.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {roleLabel(user.role)}
                  </span>
                </label>
              ))}
            </div>
          </div>
          {editing && (
            <div
              className={
                "flex items-center justify-between rounded-md border border-border px-3 py-2"
              }
            >
              <div>
                <div className="text-sm font-medium">Team status</div>
                <div className="text-xs text-muted-foreground">
                  Archived teams are unavailable for new invitations.
                </div>
              </div>
              <Switch
                checked={team.status === "active"}
                onCheckedChange={(checked) =>
                  onChange({ ...team, status: checked ? "active" : "archived" })
                }
                data-testid="team-access-team-status"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="team-access-team-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving}
            data-testid="team-access-team-save-button"
            className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          >
            {saving ? "Saving…" : editing ? "Save team" : "Create team"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccessSummary({ permissions }) {
  if (permissions?.includes("*"))
    return <Badge className="bg-emerald-600">Full access</Badge>;
  const items = permissions || [];
  return (
    <div className="flex flex-wrap gap-1">
      {items.slice(0, 2).map((item) => (
        <Badge key={item} variant="outline" className="text-[10px]">
          {item}
        </Badge>
      ))}
      {items.length > 2 && (
        <Badge variant="outline" className="text-[10px]">
          +{items.length - 2}
        </Badge>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <Card
      className="border-l-4 border-l-[#255058] p-4"
      data-testid={`team-access-metric-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      <div className="font-display text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </Card>
  );
}

function roleLabel(role, roles = []) {
  return (
    roles.find((item) => item.key === role)?.label ||
    role?.replace(/_/g, " ") ||
    "—"
  );
}
